<template>
	<div class="task-drawer-container">
		<div class="task-drawer-wrapper">
			<div class="task-drawer task-drawer-wide">
				<div class="task-drawer-header">
					<div class="lg-heading">{{ $t('tasks_list_title_unclaimed') }}</div>
					<UnreadBadge class="unclaimed-count" :unread="unclaimedCount" />
					<input
						type="image"
						src="/images/common/large-grey-close.png"
						@click="close"
						alt="close"
					/>
				</div>

				<template v-if="!isLoading">
					<template v-for="(taskday, key, index) in openTasks" :key="key">
						<div v-if="showDate(key, index)" class="task-list-header">
							{{ taskListDate(key) }}
						</div>
						<div v-if="showDate(key, index)" class="task-list-sub-header">
							{{ taskListDOW(key) }}
						</div>
						<ul class="task-list">
							<li
								v-for="task in taskday"
								:key="task.id"
								@click="editTask(task)"
							>
								<div
									class="task-list-checkbox"
									id="taskCompleteCheckbox"
									@click.stop="completeTask(task)"
								></div>
								<div class="task-li-content">
									<label class="task-li-main">{{ task.title }}</label>
									<label class="task-li-sub">
										{{ $t('tasks_creator', [task.user.fullname]) }}</label
									>
								</div>
								<div class="task-list-reminder">
									{{ displayOverDueDate(task.reminder_local) }}
								</div>
								<img
									class="task-list-arrow"
									src="/images/common/task-list-arrow.png"
								/>
							</li>
						</ul>
					</template>
				</template>

				<div v-if="isLoading" class="task-loader">
					<span>
						<img src="/images/pronto/3-dot-loader.gif" />
						<p>
							{{ $t('common_loader_loading_title') }}
						</p>
					</span>
				</div>
			</div>
		</div>

		<TaskDetail
			v-if="showTaskDetail"
			@close="showTaskDetail = false"
			@taskdeleted="deleteTask"
			@taskcomplete="completeTask"
			@closeUnclaimed="close"
			v-bind:taskdata="taskData"
		/>
	</div>
</template>

<script>
import { post } from '../helpers/api'
import store, { getChat, getStat } from '../store'
import { sortByDue, groupBy } from './Tasks'
import moment from 'moment'
import TaskDetail from './TaskDetail'
import { UnreadBadge } from './common'

export default {
	components: { TaskDetail, UnreadBadge },
	data() {
		return {
			store,
			isLoading: false,
			tasks: [],
			showTaskDetail: false,
		}
	},

	emits: ['close'],

	mounted() {
		this.loadTaskList(true)
	},

	computed: {
		openTasks() {
			let open_tasks = sortByDue(this.tasks)
			return groupBy(open_tasks, 'due')
		},

		unclaimedCount() {
			return getStat(store.currentChatId)?.unclaimed_task_count
		},
	},

	methods: {
		close() {
			this.$emit('close')
		},

		showDate(date, index) {
			const today = moment().endOf('day')
			if (index == 0) {
				return true
			} else {
				return moment(date).isAfter(today)
			}
		},

		loadTaskList(loading, oldest) {
			this.isLoading = loading

			let post_data = {
				organization_id: this.store.auth.user.organizations[0].id,
				bubble_id: this.store.currentChatId,
				completed: false,
				claimed: false,
			}

			if (oldest) {
				post_data.latest = oldest
			}

			post('/api/v1/task.list', post_data)
				.then(({ data }) => {
					if (oldest) {
						this.tasks = this.tasks.concat(data.tasks)
					} else {
						this.tasks = data.tasks
					}

					this.$nextTick(function () {
						if (data.hasmore) {
							let oldest_task = data.tasks.reduce(function (prev, curr) {
								return prev.id < curr.id ? prev : curr
							}).id

							this.loadTaskList(loading, oldest_task)
						}

						if (this.isLoading) {
							this.isLoading = false
						}
					})
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error(error)
					this.isLoading = false
				})
		},

		taskListDate(date) {
			date = moment(date + 'Z')
			const today = moment().endOf('day')
			if (date.isAfter(today)) {
				return date.format('ll').toUpperCase()
			} else {
				return this.$t('common_today').toUpperCase()
			}
		},

		taskListDOW(date) {
			const completed = moment(date + 'Z')
			const today = moment()
			if (completed.isAfter(today, 'day')) {
				return completed.format('dddd').toUpperCase()
			} else {
				return ''
			}
		},

		getAssignee(task) {
			if (task.bubble_id && !task.assigneeuser_id) {
				return getChat(task.bubble_id).displayname
			} else {
				return task.assigneeuser.fullname
			}
		},

		displayOverDueDate(date) {
			date = moment(date + 'Z')
			if (moment().isAfter(date, 'day')) {
				return date.format('ll')
			} else {
				return ''
			}
		},

		editTask(task) {
			this.taskData = task
			this.taskData.assignedTitle = this.getAssignee(task)
			this.taskData.parent = 'UnclaimedTasks.vue'
			this.showTaskDetail = true
		},

		deleteTask(data) {
			this.tasks = this.tasks.filter((task) => task.id !== data.task_id)
		},

		completeTask(task) {
			this.deleteTask({ task_id: task.id })

			post('/api/v1/task.complete', { task_id: task.id }).catch((error) => {
				console.error(error)
				this.$alert.error(error)
				this.store.tasks.open.push(task)
			})
		},
	},
}
</script>
<style lang="scss" scoped>
@import 'src/sass/variables';
.task-drawer-header {
	@include flex(32px);
}
.unread-badge {
	min-width: 36px;
	height: 36px;
	border-radius: 8px;
	background-color: rgba(16, 199, 170, 0.15);
	text-align: center;
	font-family: map-get($fonts, 500);
	font-size: 18px;
	letter-spacing: -0.3px;
	color: var(--teal-300);
}
</style>
<style lang="scss" scoped>
@import 'src/sass/variables';
@include overrideScheme('dark') {
	.task-drawer {
		background-color: var(--neutral-100);
		.lg-heading,
		.task-list-header,
		.task-list-sub-header,
		.task-li-main,
		.task-li-sub {
			color: var(--neutral-800) !important;
		}
		.task-list li {
			background-color: var(--neutral-100) !important;
			border: solid 1px var(--neutral-200);
		}
	}
	#taskLink {
		background-color: var(--neutral-100) !important;
	}
}
</style>
